/**
 * notebookSync.js - Durable notebook persistence layer
 * 
 * Provides:
 * - IndexedDB-based write-ahead log (WAL) for durability
 * - Per-page Firestore documents (no more full-notebook overwrites)
 * - Transactional revision increments
 * - Migration from legacy single-doc format
 */

import { db } from '../firebase';
import { 
  doc, 
  getDoc, 
  setDoc, 
  deleteDoc,
  onSnapshot, 
  runTransaction, 
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';

// ============================================================================
// IndexedDB Setup
// ============================================================================

const DB_NAME = 'NotebookSyncDB';
const DB_VERSION = 2;
const STORE_CACHE = 'cache';      // { key: string, data: any, rev: number, updatedAt: number }
const STORE_WAL = 'wal';          // { id: string, uid: string, op: object, version: string, createdAt: number }

let dbPromise = null;

function openDB() {
  if (dbPromise) return dbPromise;
  
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => {
      dbPromise = null;
      reject(request.error);
    };
    request.onblocked = () => {
      console.warn('[NOTEBOOK SYNC] Local database upgrade is waiting for another tab to close.');
    };
    request.onsuccess = () => {
      const database = request.result;
      database.onversionchange = () => {
        database.close();
        dbPromise = null;
      };
      resolve(database);
    };
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      
      if (!db.objectStoreNames.contains(STORE_CACHE)) {
        db.createObjectStore(STORE_CACHE, { keyPath: 'key' });
      }
      let walStore;
      if (!db.objectStoreNames.contains(STORE_WAL)) {
        walStore = db.createObjectStore(STORE_WAL, { keyPath: 'id' });
        walStore.createIndex('createdAt', 'createdAt');
        walStore.createIndex('uid', 'uid');
      } else {
        walStore = event.target.transaction.objectStore(STORE_WAL);
        if (!walStore.indexNames.contains('uid')) {
          walStore.createIndex('uid', 'uid');
        }
      }

      // Version 1 rows did not record an account id. Cache keys are not proof
      // of ownership, so leave every such row quarantined. The signed-in user
      // can explicitly claim them from the recovery banner.
      if (event.oldVersion > 0 && event.oldVersion < 2) {
        const legacyRequest = walStore.getAll();
        legacyRequest.onsuccess = () => {
          const legacyCount = (legacyRequest.result || []).filter((entry) => !entry?.uid).length;
          if (legacyCount > 0) {
            console.warn(
              `[NOTEBOOK SYNC] ${legacyCount} older local edits need account confirmation before recovery.`
            );
          }
        };
      }
    };
  });
  
  return dbPromise;
}

// ============================================================================
// Cache Operations (IndexedDB)
// ============================================================================

async function cacheGet(key) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_CACHE, 'readonly');
    const req = tx.objectStore(STORE_CACHE).get(key);
    req.onsuccess = () => resolve(req.result?.data ?? null);
    req.onerror = () => reject(req.error);
  });
}

async function cacheSet(key, data, rev = 0) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_CACHE, 'readwrite');
    tx.objectStore(STORE_CACHE).put({ key, data, rev, updatedAt: Date.now() });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function cacheDelete(key) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_CACHE, 'readwrite');
    tx.objectStore(STORE_CACHE).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function cacheGetAll() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_CACHE, 'readonly');
    const req = tx.objectStore(STORE_CACHE).getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  });
}

async function cacheClearForUser(uid) {
  assertUid(uid);
  const db = await openDB();
  const metaKey = `meta:${uid}`;
  const pagePrefix = `page:${uid}:`;

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_CACHE, 'readwrite');
    const store = tx.objectStore(STORE_CACHE);
    const req = store.getAllKeys();
    req.onsuccess = () => {
      (req.result || []).forEach((key) => {
        if (key === metaKey || (typeof key === 'string' && key.startsWith(pagePrefix))) {
          store.delete(key);
        }
      });
    };
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// ============================================================================
// WAL Operations (IndexedDB)
// ============================================================================

function createWalVersion() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function assertUid(uid) {
  if (!uid) {
    throw new Error('Notebook sync requires an authenticated user');
  }
}

async function walAppend(uid, op) {
  assertUid(uid);
  const db = await openDB();
  const now = Date.now();
  const entry = {
    id: `${uid}:${now}-${Math.random().toString(36).slice(2, 11)}`,
    uid,
    op,
    version: createWalVersion(),
    createdAt: now,
    updatedAt: now
  };
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_WAL, 'readwrite');
    tx.objectStore(STORE_WAL).add(entry);
    tx.oncomplete = () => resolve({ id: entry.id, inserted: true });
    tx.onerror = () => reject(tx.error);
  });
}

function getLatestWalId(uid, op) {
  if (op.type !== 'updatePage' || !op.pageId) {
    throw new Error('enqueueLatest currently supports updatePage operations with a pageId');
  }
  return `latest:${encodeURIComponent(uid)}:page:${encodeURIComponent(op.pageId)}`;
}

async function walPutLatest(uid, op) {
  assertUid(uid);
  const db = await openDB();
  const id = getLatestWalId(uid, op);
  const now = Date.now();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_WAL, 'readwrite');
    const store = tx.objectStore(STORE_WAL);
    const getRequest = store.get(id);
    let inserted = true;

    getRequest.onsuccess = () => {
      const existing = getRequest.result;
      inserted = !existing;

      // updatePage payloads are partial by design. Merge with the last durable
      // snapshot so Graph/Main editor updates cannot discard one another.
      const mergedOp = existing?.uid === uid && existing?.op?.type === 'updatePage'
        ? {
            ...existing.op,
            ...op,
            data: {
              ...(existing.op.data || {}),
              ...(op.data || {})
            }
          }
        : op;

      store.put({
        id,
        uid,
        op: mergedOp,
        version: createWalVersion(),
        createdAt: existing?.createdAt || now,
        updatedAt: now
      });
    };
    getRequest.onerror = () => reject(getRequest.error);
    tx.oncomplete = () => resolve({ id, inserted });
    tx.onerror = () => reject(tx.error);
  });
}

async function walGetAll(uid) {
  assertUid(uid);
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_WAL, 'readonly');
    const req = tx.objectStore(STORE_WAL).index('uid').getAll(uid);
    req.onsuccess = () => {
      const entries = req.result || [];
      entries.sort((a, b) => (
        (a.updatedAt || a.createdAt || 0) - (b.updatedAt || b.createdAt || 0)
      ));
      resolve(entries);
    };
    req.onerror = () => reject(req.error);
  });
}

async function walGetLegacyAll() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_WAL, 'readonly');
    const req = tx.objectStore(STORE_WAL).getAll();
    req.onsuccess = () => {
      const entries = (req.result || [])
        .filter((entry) => !entry?.uid)
        .sort((a, b) => (
          (a.updatedAt || a.createdAt || 0) - (b.updatedAt || b.createdAt || 0)
        ));
      resolve(entries);
    };
    req.onerror = () => reject(req.error);
  });
}

async function walClaimLegacy(uid) {
  assertUid(uid);
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_WAL, 'readwrite');
    const store = tx.objectStore(STORE_WAL);
    const req = store.getAll();
    let claimed = 0;
    const pageIds = new Set();

    req.onsuccess = () => {
      (req.result || []).forEach((entry) => {
        if (entry?.uid) return;
        claimed += 1;
        if (entry?.op?.type === 'updatePage' && entry.op.pageId) {
          pageIds.add(entry.op.pageId);
        }
        store.put({
          ...entry,
          id: `${uid}:legacy:${encodeURIComponent(String(entry.id))}`,
          uid,
          version: createWalVersion(),
          updatedAt: entry.updatedAt || entry.createdAt || Date.now(),
          recoveredFromVersion: 1
        });
        store.delete(entry.id);
      });
    };
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => resolve({ count: claimed, pageIds: Array.from(pageIds) });
    tx.onerror = () => reject(tx.error);
  });
}

async function walDelete(uid, id, version) {
  assertUid(uid);
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_WAL, 'readwrite');
    const store = tx.objectStore(STORE_WAL);
    const getRequest = store.get(id);
    let deleted = false;

    getRequest.onsuccess = () => {
      const current = getRequest.result;
      if (current?.uid === uid && (!version || current.version === version)) {
        store.delete(id);
        deleted = true;
      }
    };
    getRequest.onerror = () => reject(getRequest.error);
    tx.oncomplete = () => resolve(deleted);
    tx.onerror = () => reject(tx.error);
  });
}

async function walClear(uid) {
  assertUid(uid);
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_WAL, 'readwrite');
    const store = tx.objectStore(STORE_WAL);
    const req = store.index('uid').getAllKeys(uid);
    req.onsuccess = () => {
      (req.result || []).forEach((key) => store.delete(key));
    };
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function walCount(uid) {
  assertUid(uid);
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_WAL, 'readonly');
    const req = tx.objectStore(STORE_WAL).index('uid').count(uid);
    req.onsuccess = () => resolve(req.result || 0);
    req.onerror = () => reject(req.error);
  });
}

// ============================================================================
// Firestore Paths
// ============================================================================

function getMetaDocRef(uid) {
  return doc(db, 'notebooks', uid, 'meta', 'main');
}

function getPageDocRef(uid, pageId) {
  return doc(db, 'notebooks', uid, 'pages', pageId);
}

function getLegacyDocRef(uid) {
  return doc(db, 'notebooks', uid);
}

// ============================================================================
// Migration from Legacy Format
// ============================================================================

export async function migrateFromLegacy(uid) {
  const legacyRef = getLegacyDocRef(uid);
  const legacySnap = await getDoc(legacyRef);
  
  if (!legacySnap.exists()) {
    return { migrated: false, reason: 'no_legacy_data' };
  }
  
  const legacyData = legacySnap.data();
  
  // Already migrated?
  if (legacyData.migrated === true) {
    return { migrated: false, reason: 'already_migrated' };
  }
  
  const sections = legacyData.sections || [];
  if (!Array.isArray(sections) || sections.length === 0) {
    // Mark as migrated anyway
    await setDoc(legacyRef, { migrated: true, migratedAt: serverTimestamp() }, { merge: true });
    return { migrated: true, pagesCount: 0 };
  }
  
  
  // Build meta document (section structure without page content)
  const metaSections = sections.map(sec => ({
    id: sec.id,
    name: sec.name || 'Untitled',
    color: sec.color || '#3b82f6',
    collapsed: sec.collapsed ?? false,
    isFolder: sec.isFolder ?? false,
    parentId: sec.parentId ?? null,
    pageIds: (sec.pages || []).map(p => p.id)
  }));
  
  const metaPayload = {
    sections: metaSections,
    rev: 1,
    updatedAt: serverTimestamp(),
    migratedFromLegacy: true
  };
  
  // Collect all pages
  const allPages = [];
  for (const sec of sections) {
    for (const page of (sec.pages || [])) {
      allPages.push({
        id: page.id,
        sectionId: sec.id,
        name: page.name || 'Untitled',
        content: page.content || '',
        stickyNotes: page.stickyNotes || [],
        rev: 1,
        updatedAt: serverTimestamp()
      });
    }
  }
  
  // Write in batches (Firestore limit: 500 ops per batch)
  const BATCH_SIZE = 400;
  const metaRef = getMetaDocRef(uid);
  
  // First batch: meta + first pages
  let batch = writeBatch(db);
  batch.set(metaRef, metaPayload);
  let opsInBatch = 1;
  
  for (const page of allPages) {
    const pageRef = getPageDocRef(uid, page.id);
    batch.set(pageRef, page);
    opsInBatch++;
    
    if (opsInBatch >= BATCH_SIZE) {
      await batch.commit();
      batch = writeBatch(db);
      opsInBatch = 0;
    }
  }
  
  // Commit remaining
  if (opsInBatch > 0) {
    await batch.commit();
  }
  
  // Mark legacy as migrated (keep as backup)
  await setDoc(legacyRef, { migrated: true, migratedAt: serverTimestamp() }, { merge: true });
  
  return { migrated: true, pagesCount: allPages.length };
}

// ============================================================================
// Check if new schema exists
// ============================================================================

export async function hasNewSchema(uid) {
  const metaRef = getMetaDocRef(uid);
  const metaSnap = await getDoc(metaRef);
  return metaSnap.exists();
}

/**
 * Apply an `updateMeta` op. `updateMeta` replaces the whole section list, so an
 * op carrying an empty list (e.g. enqueued from a render that had not loaded the
 * notebook yet, possibly by an older app version and still waiting in a WAL)
 * would wipe every section. It is ignored when the notebook has sections:
 * deleting the last section goes through its own `deleteSection` op.
 */
function applyUpdateMeta(meta, data) {
  const dropsAllSections = Array.isArray(data?.sections)
    && data.sections.length === 0
    && Array.isArray(meta?.sections)
    && meta.sections.length > 0;
  if (dropsAllSections) {
    console.warn('[NOTEBOOK SYNC] Ignored a meta update that would remove every section.');
    const { sections, ...rest } = data;
    return { ...meta, ...rest };
  }
  return { ...meta, ...data };
}

function applyWeeklyReviewMeta(meta, op) {
  const next = { ...(meta || {}), sections: [...(meta?.sections || [])] };
  const folder = op.folder;
  const section = op.section;
  if (folder?.id && !next.sections.some((item) => item.id === folder.id)) {
    next.sections.push({ ...folder, pageIds: [] });
  }
  const sectionIndex = next.sections.findIndex((item) => item.id === section?.id);
  if (section?.id && sectionIndex === -1) {
    next.sections.push({ ...section, pageIds: op.pageId ? [op.pageId] : [] });
  } else if (sectionIndex >= 0 && op.pageId) {
    const current = next.sections[sectionIndex];
    const pageIds = Array.isArray(current.pageIds) ? current.pageIds : [];
    next.sections[sectionIndex] = {
      ...current,
      name: section.name || current.name,
      parentId: section.parentId ?? current.parentId,
      pageIds: pageIds.includes(op.pageId) ? pageIds : [...pageIds, op.pageId],
    };
  }
  return next;
}

// ============================================================================
// Load from Cache + WAL Replay
// ============================================================================

export async function loadFromCacheAndWAL(uid) {
  assertUid(uid);
  await waitForPendingWalWrites(uid);

  // 1. Load cached meta
  const cachedMeta = await cacheGet(`meta:${uid}`);
  
  // 2. Load cached pages
  const allCached = await cacheGetAll();
  const cachedPages = {};
  for (const item of allCached) {
    if (item.key.startsWith(`page:${uid}:`)) {
      const pageId = item.key.replace(`page:${uid}:`, '');
      cachedPages[pageId] = item.data;
    }
  }
  
  // 3. Get WAL entries
  const [walEntries, legacyWalEntries] = await Promise.all([
    walGetAll(uid),
    walGetLegacyAll()
  ]);
  
  // 4. Replay WAL on top of cached state
  let meta = cachedMeta || { sections: [], rev: 0 };
  const pages = { ...cachedPages };
  
  for (const entry of walEntries) {
    const { op } = entry;
    switch (op.type) {
      case 'updateMeta':
        meta = applyUpdateMeta(meta, op.data);
        break;
      case 'updatePage':
        pages[op.pageId] = { ...pages[op.pageId], ...op.data };
        break;
      case 'deletePage':
        delete pages[op.pageId];
        break;
      case 'addSection':
        meta.sections = [...(meta.sections || []), op.section];
        break;
      case 'deleteSection':
        meta.sections = (meta.sections || []).filter(s => s.id !== op.sectionId);
        break;
      case 'reorderSections':
        meta.sections = op.sections;
        break;
      case 'upsertWeeklyReviewMeta':
        meta = applyWeeklyReviewMeta(meta, op);
        break;
      default:
        console.warn('[NOTEBOOK SYNC] Unknown WAL op:', op.type);
    }
  }

  const pendingPageIds = Array.from(new Set(
    walEntries
      .filter((entry) => entry?.op?.type === 'updatePage' && entry.op.pageId)
      .map((entry) => entry.op.pageId)
  ));
  const hasPendingMeta = walEntries.some((entry) => (
    ['updateMeta', 'addSection', 'deleteSection', 'reorderSections', 'upsertWeeklyReviewMeta'].includes(entry?.op?.type)
  ));
  
  return {
    meta,
    pages,
    walCount: walEntries.length,
    legacyWalCount: legacyWalEntries.length,
    pendingPageIds,
    hasPendingMeta
  };
}

// ============================================================================
// Subscribe to Cloud (Real-time listeners)
// ============================================================================

export function subscribeToMeta(uid, onMeta, onError) {
  const metaRef = getMetaDocRef(uid);
  
  return onSnapshot(
    metaRef,
    { includeMetadataChanges: true },
    (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        // Update local cache
        cacheSet(`meta:${uid}`, data, data.rev || 0).catch((error) => console.error('Notebook sync error:', error?.message));
        onMeta(data, snap.metadata);
      } else {
        onMeta(null, snap.metadata);
      }
    },
    onError
  );
}

export function subscribeToPage(uid, pageId, onPage, onError) {
  if (!pageId) return () => {};
  
  const pageRef = getPageDocRef(uid, pageId);
  
  return onSnapshot(
    pageRef,
    { includeMetadataChanges: true },
    (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        // Update local cache
        cacheSet(`page:${uid}:${pageId}`, data, data.rev || 0).catch((error) => console.error('Notebook sync error:', error?.message));
        onPage(data, snap.metadata);
      } else {
        onPage(null, snap.metadata);
      }
    },
    onError
  );
}

// ============================================================================
// Save Queue - Enqueue Operations
// ============================================================================

// In-memory tracking
let flushInProgress = false;
let flushInProgressUid = null;
let onStatusChange = null;
let currentUid = null;
let pendingWalCount = 0; // Synchronous counter for beforeunload checks
const pendingWalWrites = new Set();
const pendingWalWriteCounts = new Map();
const walMutationVersions = new Map();

export function setStatusCallback(callback) {
  onStatusChange = callback;
}

export function setCurrentUser(uid) {
  currentUid = uid || null;
  pendingWalCount = 0;

  if (flushTimeout) {
    clearTimeout(flushTimeout);
    flushTimeout = null;
    flushTimeoutUid = null;
  }

  if (!currentUid) return;
  const selectedUid = currentUid;
  refreshPendingWalCount(selectedUid).then((count) => {
    if (currentUid === selectedUid && count > 0) {
      scheduleFlush(selectedUid);
    }
  }).catch((error) => {
    console.error('[NOTEBOOK SYNC] Failed to refresh pending WAL count:', error?.message);
  });
}

function notifyStatus(status, detail = null) {
  if (onStatusChange) {
    onStatusChange(status, detail);
  }
}

function notifyStatusForUser(uid, status, detail = null) {
  if (currentUid === uid) {
    notifyStatus(status, detail);
  }
}

function incrementPendingWalWrites(uid) {
  pendingWalWriteCounts.set(uid, (pendingWalWriteCounts.get(uid) || 0) + 1);
  walMutationVersions.set(uid, (walMutationVersions.get(uid) || 0) + 1);
}

function decrementPendingWalWrites(uid) {
  const next = Math.max(0, (pendingWalWriteCounts.get(uid) || 0) - 1);
  if (next === 0) {
    pendingWalWriteCounts.delete(uid);
  } else {
    pendingWalWriteCounts.set(uid, next);
  }
}

function trackWalWrite(uid, writePromise) {
  const tracked = { uid, promise: writePromise };
  incrementPendingWalWrites(uid);
  pendingWalWrites.add(tracked);
  const cleanup = () => {
    pendingWalWrites.delete(tracked);
    decrementPendingWalWrites(uid);
  };
  writePromise.then(cleanup, cleanup);
  return writePromise;
}

async function waitForPendingWalWrites(uid) {
  // A write can schedule another write in its completion callback, so keep
  // checking until the user's local persistence queue is genuinely empty.
  while (true) {
    const writes = [...pendingWalWrites]
      .filter((entry) => entry.uid === uid)
      .map((entry) => entry.promise);
    if (writes.length === 0) return;
    await Promise.all(writes);
  }
}

async function persistWalOperation(op, { latest = false } = {}) {
  const uid = currentUid;
  assertUid(uid);

  // Count the operation synchronously before IndexedDB completes so
  // beforeunload can warn even during the small transaction window.
  notifyStatusForUser(uid, 'saving');
  scheduleFlush(uid);

  const writePromise = latest ? walPutLatest(uid, op) : walAppend(uid, op);
  let result;
  try {
    result = await trackWalWrite(uid, writePromise);
    if (currentUid === uid) {
      // This is a synchronous pending indicator, not an authoritative count.
      // Exact per-user counts are refreshed directly from IndexedDB.
      pendingWalCount = Math.max(1, pendingWalCount);
    }
    notifyStatusForUser(uid, 'queued', { durable: true });
  } catch (error) {
    notifyStatusForUser(uid, 'error', { error: error.message });
    throw error;
  }

  return result.id;
}

export async function enqueue(op) {
  return persistWalOperation(op);
}

/**
 * Persist the latest full/partial page snapshot immediately, coalescing rapid
 * edits into one user+page WAL record. Cloud transmission remains debounced.
 */
export async function enqueueLatest(op) {
  return persistWalOperation(op, { latest: true });
}

export function hasPendingChangesSync() {
  if (!currentUid) return false;
  return (
    pendingWalCount > 0 ||
    (pendingWalWriteCounts.get(currentUid) || 0) > 0 ||
    (flushInProgress && flushInProgressUid === currentUid)
  );
}

export async function refreshPendingWalCount(uid = currentUid) {
  if (!uid) {
    if (!currentUid) pendingWalCount = 0;
    return 0;
  }

  try {
    let count;
    while (true) {
      const startingVersion = walMutationVersions.get(uid) || 0;
      await waitForPendingWalWrites(uid);
      count = await walCount(uid);
      const endingVersion = walMutationVersions.get(uid) || 0;
      if (startingVersion === endingVersion && (pendingWalWriteCounts.get(uid) || 0) === 0) {
        break;
      }
    }
    if (currentUid === uid) {
      pendingWalCount = count;
    }
    return count;
  } catch (error) {
    console.error('[NOTEBOOK SYNC] walCount refresh error:', error?.message);
    throw error;
  }
}

let flushTimeout = null;
let flushTimeoutUid = null;
const FLUSH_DEBOUNCE_MS = 3000;

function scheduleFlush(uid = currentUid) {
  if (!uid || uid !== currentUid) return;

  // If a timeout is already pending, reset it (extends debounce)
  if (flushTimeout) {
    clearTimeout(flushTimeout);
  }

  flushTimeoutUid = uid;
  flushTimeout = setTimeout(() => {
    const scheduledUid = flushTimeoutUid;
    flushTimeout = null;
    flushTimeoutUid = null;
    if (scheduledUid === currentUid) {
      flush().catch((error) => console.error('Notebook sync error:', error?.message));
    }
  }, FLUSH_DEBOUNCE_MS);
}

// ============================================================================
// Retry helper with exponential backoff
// ============================================================================

async function withRetry(fn, { maxAttempts = 3, baseDelay = 500, label = 'operation' } = {}) {
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const result = await fn();
    if (result.success) return result;

    if (attempt < maxAttempts) {
      const delay = baseDelay * Math.pow(2, attempt - 1) + Math.random() * 200;
      console.warn(`[NOTEBOOK SYNC] ${label} attempt ${attempt} failed, retrying in ${Math.round(delay)}ms...`);
      await new Promise(r => setTimeout(r, delay));
    } else {
      console.error(`[NOTEBOOK SYNC] ${label} failed after ${maxAttempts} attempts`);
      return result;
    }
  }
}

// ============================================================================
// Flush - Process WAL and sync to Firestore
// ============================================================================

export async function flush() {
  const uid = currentUid;
  if (!uid) {
    console.warn('[NOTEBOOK SYNC] No user, skipping flush');
    return { success: false, reason: 'no_user' };
  }
  
  if (flushInProgress) {
    // Schedule another flush after current one
    scheduleFlush(uid);
    return { success: false, reason: 'already_flushing' };
  }
  
  flushInProgress = true;
  flushInProgressUid = uid;
  notifyStatusForUser(uid, 'saving');
  
  try {
    // Include operations whose IndexedDB transaction was still completing when
    // the user clicked Save or the tab became hidden.
    await waitForPendingWalWrites(uid);
    if (currentUid !== uid) {
      return { success: false, reason: 'user_changed' };
    }

    const walEntries = await walGetAll(uid);
    
    if (walEntries.length === 0) {
      const remaining = await refreshPendingWalCount(uid);
      if (remaining === 0) {
        notifyStatusForUser(uid, 'saved');
        return { success: true, processed: 0 };
      }
      scheduleFlush(uid);
      return { success: true, processed: 0, remaining };
    }
    
    
    // Group operations by target doc
    const metaOps = [];
    const pageOps = {}; // pageId -> ops[]
    const deletePageIds = new Set();
    
    for (const entry of walEntries) {
      const { op, id, version } = entry;
      switch (op.type) {
        case 'updateMeta':
        case 'addSection':
        case 'deleteSection':
        case 'reorderSections':
        case 'upsertWeeklyReviewMeta':
          metaOps.push({ ...op, walId: id, walVersion: version });
          break;
        case 'updatePage':
          if (!pageOps[op.pageId]) pageOps[op.pageId] = [];
          pageOps[op.pageId].push({ ...op, walId: id, walVersion: version });
          break;
        case 'deletePage':
          deletePageIds.add(op.pageId);
          if (!pageOps[op.pageId]) pageOps[op.pageId] = [];
          pageOps[op.pageId].push({ ...op, walId: id, walVersion: version });
          break;
        default:
          console.warn('[NOTEBOOK SYNC] Unknown op in flush:', op.type);
      }
    }
    
    const results = { metaResult: null, pageResults: {} };
    let hadRealError = false;
    
    // 1. Flush page ops FIRST (create docs before meta references them)
    for (const [pageId, ops] of Object.entries(pageOps)) {
      if (deletePageIds.has(pageId)) {
        const result = await withRetry(
          () => deletePageDoc(uid, pageId),
          { label: `deletePage(${pageId})` }
        );
        results.pageResults[pageId] = result;
        if (result.success) {
          for (const op of ops) {
            await walDelete(uid, op.walId, op.walVersion);
          }
          await cacheDelete(`page:${uid}:${pageId}`);
        } else {
          hadRealError = true;
        }
      } else {
        const result = await withRetry(
          () => flushPageOps(uid, pageId, ops),
          { label: `flushPage(${pageId})` }
        );
        results.pageResults[pageId] = result;
        if (result.success) {
          for (const op of ops) {
            await walDelete(uid, op.walId, op.walVersion);
          }
        } else {
          hadRealError = true;
        }
      }
    }
    
    // 2. Flush meta ops AFTER pages are written
    if (metaOps.length > 0) {
      results.metaResult = await withRetry(
        () => flushMetaOps(uid, metaOps),
        { label: 'flushMeta' }
      );
      if (results.metaResult.success) {
        for (const op of metaOps) {
          await walDelete(uid, op.walId, op.walVersion);
        }
      } else {
        hadRealError = true;
      }
    }
    
    // Sync the counter with actual WAL state
    const remaining = await refreshPendingWalCount(uid);

    if (remaining === 0) {
      notifyStatusForUser(uid, 'saved');
      return { success: true, processed: walEntries.length };
    } else if (hadRealError) {
      notifyStatusForUser(uid, 'error', { remaining });
      scheduleFlush(uid);
      return { success: false, remaining };
    } else {
      scheduleFlush(uid);
      return { success: true, processed: walEntries.length, remaining };
    }
    
  } catch (error) {
    console.error('[NOTEBOOK SYNC] Flush error:', error?.message);
    notifyStatusForUser(uid, 'error', { error: error.message });
    scheduleFlush(uid);
    return { success: false, error };
  } finally {
    flushInProgress = false;
    flushInProgressUid = null;
    if (currentUid && currentUid !== uid) {
      refreshPendingWalCount(currentUid).then((count) => {
        if (count > 0) scheduleFlush(currentUid);
      }).catch((error) => console.error('Notebook sync error:', error?.message));
    }
  }
}

// ============================================================================
// Flush Meta Operations with Transaction
// ============================================================================

async function flushMetaOps(uid, ops) {
  const metaRef = getMetaDocRef(uid);
  
  try {
    const result = await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(metaRef);
      let currentMeta = snap.exists() ? snap.data() : { sections: [], rev: 0 };
      const expectedRev = currentMeta.rev || 0;
      
      // Apply all ops in order
      for (const op of ops) {
        switch (op.type) {
          case 'updateMeta':
            currentMeta = applyUpdateMeta(currentMeta, op.data);
            break;
          case 'addSection':
            currentMeta.sections = [...(currentMeta.sections || []), op.section];
            break;
          case 'deleteSection':
            currentMeta.sections = (currentMeta.sections || []).filter(s => s.id !== op.sectionId);
            break;
          case 'reorderSections':
            currentMeta.sections = op.sections;
            break;
          case 'upsertWeeklyReviewMeta':
            currentMeta = applyWeeklyReviewMeta(currentMeta, op);
            break;
          default:
            // Unknown op type, skip
            break;
        }
      }
      
      // Increment rev
      const newRev = expectedRev + 1;
      const payload = {
        ...currentMeta,
        rev: newRev,
        updatedAt: serverTimestamp()
      };
      
      transaction.set(metaRef, payload);
      return { success: true, newRev };
    });
    
    return result;
    
  } catch (error) {
    console.error('[NOTEBOOK SYNC] Meta flush error:', error?.message);
    return { success: false, error };
  }
}

// ============================================================================
// Flush Page Operations with Transaction
// ============================================================================

async function flushPageOps(uid, pageId, ops) {
  const pageRef = getPageDocRef(uid, pageId);
  
  try {
    const result = await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(pageRef);
      let currentPage = snap.exists() ? snap.data() : { id: pageId, rev: 0 };
      const expectedRev = currentPage.rev || 0;
      
      // Apply all ops in order
      for (const op of ops) {
        if (op.type === 'updatePage') {
          currentPage = { ...currentPage, ...op.data };
        }
      }
      
      // Increment rev
      const newRev = expectedRev + 1;
      const payload = {
        ...currentPage,
        id: pageId,
        rev: newRev,
        updatedAt: serverTimestamp()
      };
      
      transaction.set(pageRef, payload);
      return { success: true, newRev };
    });
    
    return result;
    
  } catch (error) {
    console.error('[NOTEBOOK SYNC] Page flush error:', error?.message);
    return { success: false, error };
  }
}

// ============================================================================
// Delete Page Document
// ============================================================================

async function deletePageDoc(uid, pageId) {
  const pageRef = getPageDocRef(uid, pageId);
  
  try {
    await deleteDoc(pageRef);
    return { success: true };
  } catch (error) {
    console.error('[NOTEBOOK SYNC] Page delete error:', error?.message);
    return { success: false, error };
  }
}

// ============================================================================
// Force Overwrite (user chose to keep local)
// ============================================================================

export async function forceOverwriteMeta(uid, metaData) {
  const metaRef = getMetaDocRef(uid);
  
  try {
    const snap = await getDoc(metaRef);
    const currentRev = snap.exists() ? (snap.data().rev || 0) : 0;
    
    const payload = {
      ...metaData,
      rev: currentRev + 1,
      updatedAt: serverTimestamp()
    };
    
    await setDoc(metaRef, payload);
    return { success: true };
  } catch (error) {
    console.error('[NOTEBOOK SYNC] Force overwrite meta error:', error?.message);
    return { success: false, error };
  }
}

export async function forceOverwritePage(uid, pageId, pageData) {
  const pageRef = getPageDocRef(uid, pageId);
  
  try {
    const snap = await getDoc(pageRef);
    const currentRev = snap.exists() ? (snap.data().rev || 0) : 0;
    
    const payload = {
      ...pageData,
      id: pageId,
      rev: currentRev + 1,
      updatedAt: serverTimestamp()
    };
    
    await setDoc(pageRef, payload);
    return { success: true };
  } catch (error) {
    console.error('[NOTEBOOK SYNC] Force overwrite page error:', error?.message);
    return { success: false, error };
  }
}

// ============================================================================
// Fetch a single page document (for integrity checks)
// ============================================================================

export async function fetchPage(uid, pageId) {
  const pageRef = getPageDocRef(uid, pageId);
  try {
    const snap = await getDoc(pageRef);
    return snap.exists() ? snap.data() : null;
  } catch (error) {
    console.error('[NOTEBOOK SYNC] fetchPage error:', pageId, error?.message);
    return null;
  }
}

// ============================================================================
// Remove orphan pageIds from meta (pages that no longer exist in Firestore)
// ============================================================================

const ENABLE_ORPHAN_CLEANUP = process.env.REACT_APP_ENABLE_ORPHAN_CLEANUP === 'true';

export async function removeOrphanPages(uid, orphanPageIds) {
  if (!ENABLE_ORPHAN_CLEANUP) {
    console.warn('[NOTEBOOK SYNC] removeOrphanPages blocked: orphan cleanup is disabled by default.');
    return { success: false, reason: 'orphan_cleanup_disabled' };
  }
  if (!orphanPageIds || orphanPageIds.length === 0) return { success: true };
  const orphanSet = new Set(orphanPageIds);

  const metaRef = getMetaDocRef(uid);
  try {
    const result = await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(metaRef);
      if (!snap.exists()) return { success: false, reason: 'no_meta' };

      const data = snap.data();
      const sections = (data.sections || []).map(sec => ({
        ...sec,
        pageIds: (sec.pageIds || []).filter(id => !orphanSet.has(id))
      }));

      const newRev = (data.rev || 0) + 1;
      transaction.set(metaRef, {
        ...data,
        sections,
        rev: newRev,
        updatedAt: serverTimestamp()
      });

      return { success: true, removedCount: orphanPageIds.length, newRev };
    });

    return result;
  } catch (error) {
    console.error('[NOTEBOOK SYNC] removeOrphanPages error:', error?.message);
    return { success: false, error };
  }
}

// ============================================================================
// Utility: Get WAL status
// ============================================================================

export async function getWALStatus(uid = currentUid) {
  if (!uid) return { count: 0, pending: false, entries: [] };
  await waitForPendingWalWrites(uid);
  const count = await walCount(uid);
  const entries = await walGetAll(uid);
  return {
    count,
    pending: count > 0,
    entries: entries.slice(0, 10) // First 10 for debugging
  };
}

export async function getLegacyWALStatus() {
  const entries = await walGetLegacyAll();
  return {
    count: entries.length,
    pending: entries.length > 0,
    entries: entries.slice(0, 10)
  };
}

/**
 * Explicitly attach quarantined v1 WAL rows to the selected account.
 * The UI requires user confirmation because older rows did not record uid.
 */
export async function claimLegacyWAL(uid = currentUid) {
  assertUid(uid);
  await waitForPendingWalWrites(uid);
  const recovery = await walClaimLegacy(uid);
  if (recovery.count === 0) return recovery;

  walMutationVersions.set(uid, (walMutationVersions.get(uid) || 0) + 1);
  await refreshPendingWalCount(uid);
  notifyStatusForUser(uid, 'queued', { durable: true, recovered: recovery.count });
  if (currentUid === uid) scheduleFlush(uid);
  return recovery;
}

// ============================================================================
// Utility: Clear the selected user's local data (for testing/reset)
// ============================================================================

export async function clearLocalData(uid = currentUid) {
  assertUid(uid);
  await waitForPendingWalWrites(uid);
  await walClear(uid);
  await cacheClearForUser(uid);
  if (currentUid === uid) pendingWalCount = 0;
}

// ============================================================================
// Flush immediately (for tab close/page switch)
// ============================================================================

export async function flushImmediate() {
  const uid = currentUid;
  if (!uid) return { success: false, reason: 'no_user' };

  // Clear any scheduled flush
  if (flushTimeout && flushTimeoutUid === uid) {
    clearTimeout(flushTimeout);
    flushTimeout = null;
    flushTimeoutUid = null;
  }
  
  await waitForPendingWalWrites(uid);
  if (currentUid !== uid) return { success: false, reason: 'user_changed' };

  // Force immediate flush
  return flush();
}

/**
 * Strict save path used by manual "Save now":
 * keep flushing until WAL is fully drained or timeout hits.
 */
export async function flushUntilSaved({ timeoutMs = 25000, pollMs = 200 } = {}) {
  const uid = currentUid;
  if (!uid) return { success: false, reason: 'no_user' };
  const startedAt = Date.now();
  let lastResult = null;

  await refreshPendingWalCount(uid);

  while (Date.now() - startedAt < timeoutMs) {
    if (currentUid !== uid) {
      return { success: false, reason: 'user_changed', lastResult };
    }
    if (!flushInProgress) {
      lastResult = await flushImmediate();
      if (lastResult?.reason === 'no_user' || lastResult?.reason === 'user_changed') {
        return { success: false, reason: lastResult.reason, lastResult };
      }
    }

    const remaining = await refreshPendingWalCount(uid);
    if (remaining === 0 && !(flushInProgress && flushInProgressUid === uid)) {
      notifyStatusForUser(uid, 'saved');
      return { success: true, remaining, lastResult };
    }

    await new Promise((resolve) => setTimeout(resolve, pollMs));
  }

  const remaining = await refreshPendingWalCount(uid);
  notifyStatusForUser(uid, 'error', { error: 'save_timeout', remaining });
  return { success: false, reason: 'timeout', remaining, lastResult };
}

// ============================================================================
// Check if there are pending changes
// ============================================================================

export async function hasPendingChanges() {
  const uid = currentUid;
  if (!uid) return false;
  const count = await refreshPendingWalCount(uid);
  return count > 0 || (flushInProgress && flushInProgressUid === uid);
}
